import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import { Plus, Trash2, ArrowUp, ArrowDown, Sparkles, Loader2, FileInput, ArrowUpDown } from 'lucide-react';

const LessonEditorNew = ({ lesson, onBack, isNew, isDuplicate }) => {
  const { toast } = useToast();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Initialize form data
  const defaultData = {
    lesson_id: '',
    day_number: 1,
    passage_id: 'eph4',
    session_id: null,
    title: '',
    description: '',
    verses: '',
    prayer_checklist: [
      'Praise God',
      'Give Thanks to Him',
      'Ask Him to reveal His will and teach you',
      'Read all of Ephesians 4 from your booklet',
      'Meditate on His Word'
    ],
    closing_prayer: [
      'Listen for God to speak',
      'Confess your sins',
      'Consider how you will be obedient',
      'What change does God desire from you?',
      'What is your new God centered habit to work on?',
      'Praise God'
    ],
    scripture: '',
    reference: '',
    key_words: [],
    memorization: [],
    activities: [],
    trivia: []
  };

  const initialData = lesson ? {
    ...defaultData,
    ...lesson,
    // Only use defaults if lesson doesn't have these fields
    prayer_checklist: lesson.prayer_checklist || defaultData.prayer_checklist,
    closing_prayer: lesson.closing_prayer || defaultData.closing_prayer
  } : defaultData;

  if (isDuplicate) {
    initialData.session_id = null;
    initialData.verses = '';  // Clear verses field for duplicated lessons
  }

  const [formData, setFormData] = useState(initialData);
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState({ type: null, index: null });
  const [keyWordsText, setKeyWordsText] = useState(
    Array.isArray(initialData.key_words) ? initialData.key_words.join(', ') : ''
  );
  const [allLessons, setAllLessons] = useState([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importDialogType, setImportDialogType] = useState(''); // 'trivia', 'verses', or 'activities'
  const [selectedLessonsForImport, setSelectedLessonsForImport] = useState([]);
  
  // Fetch sessions
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await api.get('/admin/study-sessions', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSessions(response.data.sessions || []);
      } catch (error) {
        console.error('Failed to fetch sessions:', error);
      }
    };
    fetchSessions();
  }, []);

  // Fetch all lessons for import functionality
  useEffect(() => {
    const fetchAllLessons = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await api.get('/admin/lessons', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Exclude current lesson from the list, don't filter by content yet
        const otherLessons = response.data.lessons.filter(
          l => l.lesson_id !== formData.lesson_id
        );
        setAllLessons(otherLessons);
      } catch (error) {
        console.error('Failed to fetch lessons:', error);
      }
    };
    fetchAllLessons();
  }, [formData.lesson_id]);

  // Update field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Prayer checklist functions
  const addPrayerItem = () => {
    updateField('prayer_checklist', [...formData.prayer_checklist, '']);
  };

  const updatePrayerItem = (index, value) => {
    const updated = [...formData.prayer_checklist];
    updated[index] = value;
    updateField('prayer_checklist', updated);
  };

  const removePrayerItem = (index) => {
    updateField('prayer_checklist', formData.prayer_checklist.filter((_, i) => i !== index));
  };

  // Closing prayer functions
  const addClosingPrayerItem = () => {
    updateField('closing_prayer', [...formData.closing_prayer, '']);
  };

  const updateClosingPrayerItem = (index, value) => {
    const updated = [...formData.closing_prayer];
    updated[index] = value;
    updateField('closing_prayer', updated);
  };

  const removeClosingPrayerItem = (index) => {
    updateField('closing_prayer', formData.closing_prayer.filter((_, i) => i !== index));
  };

  // Memorization functions
  const addVerse = () => {
    updateField('memorization', [
      ...formData.memorization,
      { verse_number: '', verse_text: '', mnemonic: '', mnemonic_html: null }
    ]);
  };

  const updateVerse = (index, field, value) => {
    const updated = [...formData.memorization];
    updated[index][field] = value;
    updateField('memorization', updated);
  };

  const removeVerse = (index) => {
    updateField('memorization', formData.memorization.filter((_, i) => i !== index));
  };

  const moveVerseUp = (index) => {
    if (index === 0) return;
    const updated = [...formData.memorization];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updateField('memorization', updated);
  };

  const moveVerseDown = (index) => {
    if (index === formData.memorization.length - 1) return;
    const updated = [...formData.memorization];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updateField('memorization', updated);
  };

  // AI Hyperlink mnemonic
  const generateMnemonicHyperlink = async (index) => {
    const verse = formData.memorization[index];
    if (!verse.mnemonic || !verse.verse_text) {
      toast({
        title: "Missing Data",
        description: "Please add both verse text and mnemonic before generating hyperlinks",
        variant: "destructive"
      });
      return;
    }

    setGenerating({ type: 'mnemonic', index });
    try {
      const response = await api.post('/ai/hyperlink-mnemonic', {
        mnemonic_text: verse.mnemonic,
        verse_text: verse.verse_text
      });
      updateVerse(index, 'mnemonic_html', response.data.hyperlinked_html);
      toast({
        title: "Success",
        description: "Mnemonic hyperlinks generated!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate hyperlinks: " + (error.response?.data?.detail || error.message),
        variant: "destructive"
      });
    } finally {
      setGenerating({ type: null, index: null });
    }
  };

  // Activity functions
  const addActivity = () => {
    updateField('activities', [
      ...formData.activities,
      {
        verse_number: '',
        verse_text: '',
        activity_text: '',
        answers: [],
        num_words_hidden: 5,
        is_randomized: true
      }
    ]);
  };

  const updateActivity = (index, field, value) => {
    const updated = [...formData.activities];
    updated[index][field] = value;
    updateField('activities', updated);
  };

  const removeActivity = (index) => {
    updateField('activities', formData.activities.filter((_, i) => i !== index));
  };

  // AI Generate fill-in-the-blank
  const generateFillInBlank = async (index) => {
    const activity = formData.activities[index];
    if (!activity.verse_text || !activity.verse_number) {
      toast({
        title: "Missing Data",
        description: "Please add verse number and verse text before generating",
        variant: "destructive"
      });
      return;
    }

    if (!activity.num_words_hidden || activity.num_words_hidden < 1) {
      toast({
        title: "Invalid Input",
        description: "Please specify number of words to hide (minimum 1)",
        variant: "destructive"
      });
      return;
    }

    setGenerating({ type: 'fillblank', index });
    try {
      const response = await api.post('/ai/generate-fill-blank', {
        verse_text: activity.verse_text,
        verse_reference: activity.verse_number,
        num_words: parseInt(activity.num_words_hidden)
      });
      
      // Check for errors
      if (response.data.error) {
        toast({
          title: "Generation Error",
          description: response.data.error,
          variant: "destructive"
        });
        return;
      }
      
      const requested = parseInt(activity.num_words_hidden);
      const generated = response.data.answers.length;
      
      updateActivity(index, 'activity_text', response.data.text);
      updateActivity(index, 'answers', response.data.answers);
      // Keep the user's original input - don't change it!
      
      // Warn if count doesn't match
      if (generated !== requested) {
        toast({
          title: "Different Count",
          description: `AI generated ${generated} words instead of ${requested}. Click "AI Generate" again to retry or keep this version.`,
          variant: "default"
        });
      } else {
        toast({
          title: "Success",
          description: `Generated fill-in-the-blank with ${generated} hidden words!`
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate activity: " + (error.response?.data?.detail || error.message),
        variant: "destructive"
      });
    } finally {
      setGenerating({ type: null, index: null });
    }
  };

  // Trivia functions
  const addTriviaQuestion = () => {
    updateField('trivia', [
      ...formData.trivia,
      { question: '', answer: '', reference: formData.reference || '', points: 15 }
    ]);
  };

  const updateTriviaQuestion = (index, field, value) => {
    const updated = [...formData.trivia];
    updated[index][field] = value;
    updateField('trivia', updated);
  };

  const removeTriviaQuestion = (index) => {
    updateField('trivia', formData.trivia.filter((_, i) => i !== index));
  };

  // Generic import function
  const handleImport = () => {
    if (selectedLessonsForImport.length === 0) {
      toast({
        title: "No Lessons Selected",
        description: "Please select at least one lesson to import from",
        variant: "destructive"
      });
      return;
    }

    let importedItems = [];
    let itemType = '';
    let fieldName = '';

    if (importDialogType === 'trivia') {
      selectedLessonsForImport.forEach(lessonId => {
        const lesson = allLessons.find(l => l.lesson_id === lessonId);
        if (lesson && lesson.trivia) {
          importedItems.push(...lesson.trivia);
        }
      });
      itemType = 'question';
      fieldName = 'trivia';
    } else if (importDialogType === 'verses') {
      selectedLessonsForImport.forEach(lessonId => {
        const lesson = allLessons.find(l => l.lesson_id === lessonId);
        if (lesson && lesson.memorization) {
          importedItems.push(...lesson.memorization);
        }
      });
      itemType = 'verse';
      fieldName = 'memorization';
    } else if (importDialogType === 'activities') {
      selectedLessonsForImport.forEach(lessonId => {
        const lesson = allLessons.find(l => l.lesson_id === lessonId);
        if (lesson && lesson.activities) {
          importedItems.push(...lesson.activities);
        }
      });
      itemType = 'activity';
      fieldName = 'activities';
    }

    // Append imported items to existing ones
    updateField(fieldName, [...formData[fieldName], ...importedItems]);
    
    toast({
      title: "Import Successful",
      description: `Imported ${importedItems.length} ${itemType}${importedItems.length !== 1 ? 's' : ''} from ${selectedLessonsForImport.length} lesson(s)`,
    });

    setShowImportDialog(false);
    setImportDialogType('');
    setSelectedLessonsForImport([]);
  };

  // Sort trivia questions by reference
  const sortTriviaByReference = () => {
    const sorted = [...formData.trivia].sort((a, b) => {
      // Simple string comparison of references
      return (a.reference || '').localeCompare(b.reference || '');
    });
    updateField('trivia', sorted);
    toast({
      title: "Questions Sorted",
      description: "Trivia questions have been sorted by reference",
    });
  };

  // Move trivia question up
  const moveTriviaUp = (index) => {
    if (index === 0) return;
    const updated = [...formData.trivia];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updateField('trivia', updated);
  };

  // Move trivia question down
  const moveTriviaDown = (index) => {
    if (index === formData.trivia.length - 1) return;
    const updated = [...formData.trivia];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updateField('trivia', updated);
  };

  // AI Generate trivia questions
  const generateTriviaQuestions = async (append = false) => {
    if (!formData.scripture || !formData.reference) {
      toast({
        title: "Missing Data",
        description: "Please add scripture text and reference before generating trivia",
        variant: "destructive"
      });
      return;
    }

    // Check if we have memorization verses to generate per-verse questions
    if (!formData.memorization || formData.memorization.length === 0) {
      toast({
        title: "Missing Verses",
        description: "Please add verses in 'Memorize the Word' section first. AI will generate 3 questions per verse.",
        variant: "destructive"
      });
      return;
    }

    setGenerating({ type: 'trivia', index: null });
    try {
      let allQuestions = [];
      
      // Get existing question texts to avoid duplicates
      const existingQuestionTexts = formData.trivia.map(q => q.question);
      
      // Generate 3 questions per verse
      for (const verse of formData.memorization) {
        if (verse.verse_text && verse.verse_number) {
          const response = await api.post('/ai/generate-trivia', {
            verse_text: verse.verse_text,
            verse_reference: verse.verse_number,
            num_questions: 3,
            existing_questions: existingQuestionTexts
          });
          allQuestions = [...allQuestions, ...response.data];
          // Add newly generated questions to existing list to check against in next iteration
          existingQuestionTexts.push(...response.data.map(q => q.question));
        }
      }
      
      if (append) {
        // Add to existing questions
        updateField('trivia', [...formData.trivia, ...allQuestions]);
      } else {
        // Replace all questions
        updateField('trivia', allQuestions);
      }
      
      toast({
        title: "Success",
        description: append 
          ? `Added ${allQuestions.length} trivia questions (3 per verse)!` 
          : `Generated ${allQuestions.length} trivia questions (3 per verse)!`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate trivia: " + (error.response?.data?.detail || error.message),
        variant: "destructive"
      });
    } finally {
      setGenerating({ type: null, index: null });
    }
  };

  // Save lesson
  const handleSave = async () => {
    // Validation
    if (!formData.lesson_id || !formData.title || !formData.reference || !formData.scripture) {
      toast({
        title: "Missing Required Fields",
        description: "Please fill in Lesson ID, Title, Reference, and Scripture",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      // Parse key words text to array before saving
      const words = keyWordsText.split(',').map(w => w.trim()).filter(w => w);
      
      const dataToSave = { 
        ...formData,
        key_words: words
      };
      delete dataToSave._id;

      if (isNew) {
        await api.post('/admin/lessons', dataToSave, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (dataToSave.session_id) {
          try {
            const sessionResponse = await api.get(`/admin/study-sessions/${dataToSave.session_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const session = sessionResponse.data;
            const updatedLessonIds = [...(session.lesson_ids || []), dataToSave.lesson_id];
            await api.put(`/admin/study-sessions/${dataToSave.session_id}`,
              { lesson_ids: updatedLessonIds },
              { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
          } catch (sessionError) {
            console.error('Failed to add lesson to session:', sessionError);
          }
        }

        toast({
          title: "Success",
          description: "Lesson created successfully"
        });
      } else {
        await api.put(`/admin/lessons/${lesson.lesson_id}`, dataToSave, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        toast({
          title: "Success",
          description: "Lesson updated successfully"
        });
      }

      onBack();
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to save lesson",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {isDuplicate ? 'Duplicate Lesson' : isNew ? 'Create New Lesson' : `Edit ${lesson?.lesson_id}`}
          </h1>
          <Button onClick={onBack} variant="outline">Back</Button>
        </div>

        {/* Section 1: Basic Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>1. Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lesson_id">Lesson ID *</Label>
                <Input
                  id="lesson_id"
                  value={formData.lesson_id}
                  onChange={(e) => updateField('lesson_id', e.target.value)}
                  placeholder="e.g., day1, day15"
                  disabled={!isNew && !isDuplicate}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="day_number">Day Number *</Label>
                <Input
                  id="day_number"
                  type="number"
                  value={formData.day_number}
                  onChange={(e) => updateField('day_number', parseInt(e.target.value) || 1)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="verses">Verses</Label>
              <Input
                id="verses"
                value={formData.verses}
                onChange={(e) => updateField('verses', e.target.value)}
                placeholder="e.g., Verses 1-3"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="reference">Scripture Reference *</Label>
                <Input
                  id="reference"
                  value={formData.reference}
                  onChange={(e) => updateField('reference', e.target.value)}
                  placeholder="e.g., Ephesians 4:1-3"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="session_id">Assign to Study Session</Label>
                <select
                  id="session_id"
                  value={formData.session_id || ''}
                  onChange={(e) => updateField('session_id', e.target.value || null)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">-- Unassigned --</option>
                  {sessions.map((session) => (
                    <option key={session.session_id} value={session.session_id}>
                      {session.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Lesson Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Enter lesson title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Optional lesson description"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="scripture">Scripture Text *</Label>
              <Textarea
                id="scripture"
                value={formData.scripture}
                onChange={(e) => updateField('scripture', e.target.value)}
                placeholder="Enter the full scripture passage"
                rows={5}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Prayer Time Checklist */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>2. Prayer Time Checklist</CardTitle>
              <Button onClick={addPrayerItem} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {formData.prayer_checklist.map((item, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => updatePrayerItem(index, e.target.value)}
                  placeholder="Prayer checklist item"
                />
                <Button
                  onClick={() => removePrayerItem(index)}
                  variant="destructive"
                  size="icon"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Section 3: Key Words */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>3. Key Words</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="key_words">Key Words (comma-separated)</Label>
              <Input
                id="key_words"
                value={keyWordsText}
                onChange={(e) => setKeyWordsText(e.target.value)}
                onBlur={(e) => {
                  // Only parse to array on blur
                  const words = e.target.value.split(',').map(w => w.trim()).filter(w => w);
                  updateField('key_words', words);
                }}
                placeholder="e.g., unity, peace, love, grace"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Memorize the Word */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>4. Memorize the Word (Verses with Mnemonics)</CardTitle>
              <Button
                onClick={() => {
                  setImportDialogType('verses');
                  setShowImportDialog(true);
                }}
                variant="outline"
                size="sm"
              >
                <FileInput className="w-4 h-4 mr-2" />
                Import Verses
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.memorization.map((verse, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold">Verse {index + 1}</h4>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => moveVerseUp(index)}
                      variant="outline"
                      size="sm"
                      disabled={index === 0}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => moveVerseDown(index)}
                      variant="outline"
                      size="sm"
                      disabled={index === formData.memorization.length - 1}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => removeVerse(index)}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Verse Number/Reference</Label>
                  <Input
                    value={verse.verse_number}
                    onChange={(e) => updateVerse(index, 'verse_number', e.target.value)}
                    placeholder="e.g., Ephesians 4:1"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Verse Text</Label>
                  <Textarea
                    value={verse.verse_text}
                    onChange={(e) => updateVerse(index, 'verse_text', e.target.value)}
                    placeholder="Enter the verse text"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Mnemonic Device</Label>
                  <Input
                    value={verse.mnemonic}
                    onChange={(e) => updateVerse(index, 'mnemonic', e.target.value)}
                    placeholder="e.g., T I, a p f s t l..."
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => generateMnemonicHyperlink(index)}
                    variant="outline"
                    size="sm"
                    disabled={generating.type === 'mnemonic' && generating.index === index}
                  >
                    {generating.type === 'mnemonic' && generating.index === index ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />AI Hyperlink Keywords</>
                    )}
                  </Button>
                </div>

                {verse.mnemonic_html && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded">
                    <p className="text-sm font-semibold text-green-900 mb-1">Hyperlinked Version:</p>
                    <div
                      className="text-sm text-green-800"
                      dangerouslySetInnerHTML={{ __html: verse.mnemonic_html }}
                    />
                  </div>
                )}
              </div>
            ))}
            
            {/* Add Verse button at bottom */}
            <div className="pt-4 border-t">
              <Button onClick={addVerse} size="sm" className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Verse
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Activities (Fill-in-the-Blank) */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>5. Activities (Fill-in-the-Blank)</CardTitle>
              <Button
                onClick={() => {
                  setImportDialogType('activities');
                  setShowImportDialog(true);
                }}
                variant="outline"
                size="sm"
              >
                <FileInput className="w-4 h-4 mr-2" />
                Import Activities
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.activities.map((activity, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold">Activity {index + 1}</h4>
                  <Button
                    onClick={() => removeActivity(index)}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Verse Number/Reference</Label>
                    <Input
                      value={activity.verse_number}
                      onChange={(e) => updateActivity(index, 'verse_number', e.target.value)}
                      placeholder="e.g., Ephesians 4:1"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Number of Words to Hide</Label>
                    <Input
                      type="number"
                      value={activity.num_words_hidden}
                      onChange={(e) => updateActivity(index, 'num_words_hidden', parseInt(e.target.value) || 5)}
                      min="1"
                      placeholder="e.g., 5, 8, 12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Verse Text</Label>
                  <Textarea
                    value={activity.verse_text}
                    onChange={(e) => updateActivity(index, 'verse_text', e.target.value)}
                    placeholder="Enter the verse text"
                    rows={3}
                  />
                </div>

                <Button
                  onClick={() => generateFillInBlank(index)}
                  variant="outline"
                  disabled={generating.type === 'fillblank' && generating.index === index}
                >
                  {generating.type === 'fillblank' && generating.index === index ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" />AI Generate Fill-in-the-Blank</>
                  )}
                </Button>

                {activity.activity_text && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded space-y-2">
                    <p className="text-sm font-semibold text-blue-900">Generated Activity:</p>
                    <p className="text-sm text-blue-800">{activity.activity_text}</p>
                    <p className="text-xs text-blue-600">
                      Answers: {activity.answers.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            ))}
            
            {/* Add Activity button at bottom */}
            <div className="pt-4 border-t">
              <Button onClick={addActivity} size="sm" className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Activity
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 6: Trivia */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>6. Trivia Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Action buttons at top */}
            <div className="flex gap-2 pb-4 border-b">
              <Button
                onClick={() => {
                  setImportDialogType('trivia');
                  setShowImportDialog(true);
                }}
                variant="outline"
                size="sm"
              >
                <FileInput className="w-4 h-4 mr-2" />
                Import Questions
              </Button>
              <Button
                onClick={sortTriviaByReference}
                variant="outline"
                size="sm"
                disabled={formData.trivia.length === 0}
              >
                <ArrowUpDown className="w-4 h-4 mr-2" />
                Sort by Reference
              </Button>
              <Button
                onClick={() => generateTriviaQuestions(false)}
                variant="outline"
                size="sm"
                disabled={generating.type === 'trivia'}
              >
                {generating.type === 'trivia' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />AI Generate</>
                )}
              </Button>
              {formData.trivia.length > 0 && (
                <Button
                  onClick={() => generateTriviaQuestions(true)}
                  variant="outline"
                  size="sm"
                  disabled={generating.type === 'trivia'}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  AI Add More
                </Button>
              )}
            </div>
            {formData.trivia.length === 0 && (
              <div className="text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-600 mb-2">No trivia questions yet</p>
                <p className="text-sm text-gray-500">Click "AI Generate" to create 3-5 questions automatically, or "Manual Add" to create your own</p>
              </div>
            )}
            {formData.trivia.length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  💡 <strong>Tip:</strong> All fields below are editable. You can modify AI-generated questions or add your own!
                </p>
              </div>
            )}
            {formData.trivia.map((question, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold">Question {index + 1}</h4>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => moveTriviaUp(index)}
                      variant="outline"
                      size="sm"
                      disabled={index === 0}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => moveTriviaDown(index)}
                      variant="outline"
                      size="sm"
                      disabled={index === formData.trivia.length - 1}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => removeTriviaQuestion(index)}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Question</Label>
                  <Textarea
                    value={question.question}
                    onChange={(e) => updateTriviaQuestion(index, 'question', e.target.value)}
                    placeholder="Enter trivia question"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Answer</Label>
                  <Input
                    value={question.answer}
                    onChange={(e) => updateTriviaQuestion(index, 'answer', e.target.value)}
                    placeholder="Enter answer"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Reference</Label>
                    <Input
                      value={question.reference}
                      onChange={(e) => updateTriviaQuestion(index, 'reference', e.target.value)}
                      placeholder="e.g., John 3:16"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Points</Label>
                    <select
                      value={question.points}
                      onChange={(e) => updateTriviaQuestion(index, 'points', parseInt(e.target.value))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value={10}>10 (Easiest)</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                      <option value={25}>25</option>
                      <option value={30}>30 (Hardest)</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Manual Add button at bottom */}
            <div className="pt-4 border-t">
              <Button onClick={addTriviaQuestion} size="sm" className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Manual Add Question
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Generic Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {importDialogType === 'trivia' && 'Import Trivia Questions from Other Lessons'}
                {importDialogType === 'verses' && 'Import Verses from Other Lessons'}
                {importDialogType === 'activities' && 'Import Activities from Other Lessons'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {(() => {
                const filteredLessons = allLessons.filter(lesson => {
                  if (importDialogType === 'trivia') return lesson.trivia && lesson.trivia.length > 0;
                  if (importDialogType === 'verses') return lesson.memorization && lesson.memorization.length > 0;
                  if (importDialogType === 'activities') return lesson.activities && lesson.activities.length > 0;
                  return false;
                });

                if (filteredLessons.length === 0) {
                  return (
                    <p className="text-gray-500 text-center py-8">
                      No other lessons with {importDialogType === 'trivia' ? 'trivia questions' : importDialogType === 'verses' ? 'verses' : 'activities'} found
                    </p>
                  );
                }

                return (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 mb-3">
                      Select lessons to import from. All {importDialogType === 'trivia' ? 'questions' : importDialogType === 'verses' ? 'verses' : 'activities'} from selected lessons will be added.
                    </p>
                    {filteredLessons.map((lesson) => {
                      let count = 0;
                      if (importDialogType === 'trivia') count = lesson.trivia?.length || 0;
                      else if (importDialogType === 'verses') count = lesson.memorization?.length || 0;
                      else if (importDialogType === 'activities') count = lesson.activities?.length || 0;

                      return (
                        <div key={lesson.lesson_id} className="flex items-center space-x-3 p-3 border rounded hover:bg-gray-50">
                          <input
                            type="checkbox"
                            id={`import-${lesson.lesson_id}`}
                            checked={selectedLessonsForImport.includes(lesson.lesson_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedLessonsForImport([...selectedLessonsForImport, lesson.lesson_id]);
                              } else {
                                setSelectedLessonsForImport(selectedLessonsForImport.filter(id => id !== lesson.lesson_id));
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <label htmlFor={`import-${lesson.lesson_id}`} className="flex-1 cursor-pointer">
                            <div className="font-semibold">{lesson.title || lesson.lesson_id}</div>
                            <div className="text-sm text-gray-500">
                              {lesson.reference} • {count} {importDialogType === 'trivia' ? 'question' : importDialogType === 'verses' ? 'verse' : 'activity'}{count !== 1 ? 's' : ''}
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowImportDialog(false);
                setImportDialogType('');
                setSelectedLessonsForImport([]);
              }}>
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={selectedLessonsForImport.length === 0}>
                Import {selectedLessonsForImport.length > 0 && `(${selectedLessonsForImport.length} lesson${selectedLessonsForImport.length > 1 ? 's' : ''})`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Section 7: Closing Prayer Checklist */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>7. Closing Prayer Checklist</CardTitle>
              <Button onClick={addClosingPrayerItem} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {formData.closing_prayer.map((item, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => updateClosingPrayerItem(index, e.target.value)}
                  placeholder="Closing prayer checklist item"
                />
                <Button
                  onClick={() => removeClosingPrayerItem(index)}
                  variant="destructive"
                  size="icon"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Save Buttons */}
        <div className="flex gap-4 mb-6">
          <Button onClick={handleSave} disabled={saving} className="flex-1" size="lg">
            {saving ? 'Saving...' : 'Save Lesson'}
          </Button>
          <Button onClick={onBack} variant="outline" size="lg">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LessonEditorNew;
